'use client'

import ChevronRight from '@/components/icons/chevron-right'
import HomeIcon from '@/components/icons/home'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import getManagerBreadcrumbData from '../actions/get-manager-breadcrumb-data'

export default function ManagerBreadcrumb() {
  const [loading, setLoading] = useState(true)
  const [breadcrumbData, setBreadcrumbData] = useState(null)

  const params = useParams()
  const paths = usePathname()

  useEffect(() => {
    const storage = localStorage.getItem('breadcrumbs')
    setBreadcrumbData(
      storage && storage !== 'undefined' ? JSON.parse(storage) : null,
    )
  }, [])

  useEffect(() => {
    async function fetchBreadcrumbData() {
      setLoading(true)
      const data = await getManagerBreadcrumbData(params)
      setLoading(false)
      localStorage.setItem('breadcrumbs', JSON.stringify(data))
      setBreadcrumbData(data)
    }

    fetchBreadcrumbData()
  }, [params])

  const breadcrumbs = useMemo(() => {
    const pathNames = paths.split('/').filter((path) => path)
    // Remove manage path
    pathNames.shift()

    const crumbs = pathNames
      .filter((p) => !['racing', 'session'].includes(p))
      .slice(1)
      .map((path, index) => {
        switch (path) {
          case breadcrumbData?.community?.id:
            return {
              name: breadcrumbData.community.name,
              href: `/manage/${path}/racing`,
            }

          case breadcrumbData?.championship?.id:
            return {
              name: breadcrumbData.championship.name,
              href: `/manage/${breadcrumbData.community.id}/racing/${path}/overview`,
            }

          case breadcrumbData?.event?.id:
            return {
              name: breadcrumbData.event.name,
              href: `/manage/${breadcrumbData.community.id}/racing/${breadcrumbData.championship.id}/results/${path}`,
            }

          case breadcrumbData?.session?.id:
            return {
              name: breadcrumbData.session.name || breadcrumbData.session.type,
              href: `/manage/${breadcrumbData.community.id}/racing/${breadcrumbData.championship.id}/results/${breadcrumbData.event.id}/session/${path}`,
            }

          default:
            const href = `/manage/${pathNames.slice(0, index + 2).join('/')}/${path}`

            return {
              name: path.replace('-', ' '),
              href,
              hasLoadingState: true,
            }
        }
      })

    return crumbs.filter((crumb) => crumb)
  }, [breadcrumbData, paths])

  return (
    <div className="mb-3 hidden flex-wrap items-center md:flex">
      <Link
        href="/manage"
        className={cn(
          'group flex cursor-pointer items-center gap-2 p-1 text-cap font-semibold text-shades-0 hover:text-neutral-blue-400 tablet:text-p-1',
          {
            'text-neutral-blue-400 hover:text-neutral-blue-50':
              breadcrumbs.length > 0,
          },
        )}
      >
        <HomeIcon
          className={cn('h-4 stroke-2 tablet:h-6', {
            'stroke-shades-0 group-hover:stroke-neutral-blue-400':
              breadcrumbs.length === 0,
            'stroke-neutral-blue-400 group-hover:stroke-neutral-blue-50':
              breadcrumbs.length > 0,
          })}
        />
        Home
      </Link>

      <div className="flex flex-wrap items-center">
        {breadcrumbs.map((breadcrumb, index) => (
          <div key={`breadcrumb-${index}`} className="flex items-center">
            <ChevronRight className="aspect-square h-4 stroke-neutral-blue-400 stroke-[2px]" />

            <Link
              className={cn(
                'cursor-pointer whitespace-nowrap p-1 text-cap font-semibold capitalize hover:text-neutral-blue-50 tablet:text-p-1',
                {
                  'text-shades-0': index === breadcrumbs.length - 1,
                  'text-neutral-blue-400': index !== breadcrumbs.length - 1,
                },
              )}
              href={breadcrumb.href}
            >
              {breadcrumb.hasLoadingState && loading ? (
                <Skeleton className="h-4 w-[100px]" />
              ) : (
                breadcrumb.name
              )}
            </Link>
          </div>
        ))}
      </div>
    </div>
  )
}
